// SFCC-11012 (ANALYTICS-16/-17/-18) — pure builder for the Segment `View Item`
// payload. Ports SFRA `segmentLayerHelper.productDetailSegmentLayerViewHelper`
// (:389-427). Kept side-effect-free + exported so the tracker resolves the raw
// SCAPI product/variant data and this stays unit testable (mirrors the
// `buildSegmentViewCategory` precedent).
//
// `device` + `sales_channel` are intentionally NOT built here — they are
// client-only (UA + site context) and appended by `trackPageEvent`.
import type { PathRecord } from "~/lib/app.types";

export type SegmentViewItem = {
  product_id: string;
  sku: string;
  title: string;
  brand: string;
  variant: string;
  price: number;
  stock_level: number;
  local_currency: string;
  marketing_sensitive: boolean;
  image_url: string;
  category_level_1: string;
  category_level_2: string;
  source_origin: string;
};

// Clean input — decoupled from the SCAPI product shape so the tracker does the
// SCAPI mapping and the builder stays pure.
export type ViewItemInput = {
  /** Effective SKU being viewed: the selected variant id, else the product id. */
  sku: string;
  /**
   * SFRA `getMasterAndVariantID` (:596-626): the master id for masters/variants,
   * `""` for standalone (standard/bundle/set/item) products.
   */
  masterId: string;
  name?: string | null;
  brand?: string | null;
  /** Sales price as a number (SFRA `parseFloat(_gtmPrice)`). */
  price?: number | null;
  currency?: string | null;
  /** ATS for the viewed SKU (SFRA `availabilityModel.inventoryRecord.ATS.value`). */
  stockLevel?: number | null;
  /** Product custom attr `c_marketing_sensitive` (sparse — absent ⇒ false). */
  marketingSensitive?: boolean | null;
  imageUrl?: string | null;
  /** Category breadcrumb, root→leaf (`parentCategoryTree`). */
  categories: PathRecord[];
  /** Surface the view fired from, e.g. `"PDP"` / `"QuickView"`. Blank if a caller omits it. */
  sourceOrigin?: string | null;
};

/**
 * Assemble the `View Item` payload. `product_id`/`sku`/`variant` follow SFRA
 * exactly: `sku === variant` (the viewed SKU), `product_id` is the master id
 * (`""` for standalone). `marketing_sensitive` is coerced to a boolean — the
 * SCAPI custom attr is omitted when unset, so absence → false, matching SFRA's
 * `!!apiProduct.custom.marketing_sensitive`. `local_currency` defaults to USD
 * (all three sites are USD per `sites.ts`).
 */
export const buildSegmentViewItem = ({
  sku,
  masterId,
  name,
  brand,
  price,
  currency,
  stockLevel,
  marketingSensitive,
  imageUrl,
  categories,
  sourceOrigin,
}: ViewItemInput): SegmentViewItem => ({
  product_id: masterId,
  sku,
  title: name ?? "",
  brand: brand ?? "",
  variant: sku,
  price: typeof price === "number" ? price : 0,
  stock_level: typeof stockLevel === "number" ? stockLevel : 0,
  local_currency: currency ?? "USD",
  marketing_sensitive: !!marketingSensitive,
  image_url: imageUrl ?? "",
  category_level_1: categories[0]?.name ?? "",
  category_level_2: categories[1]?.name ?? "",
  source_origin: sourceOrigin ?? "",
});
